import classNames from 'classnames';
import type { ReactElement, Ref, RefObject } from 'react';
import { useMemo, useRef } from 'react';
import styles from './PrimaryPagination.module.scss';
import { setupTransitionInTimeline } from './PrimaryPagination.transitions';
import type { PrimaryPaginationProps, PrimaryPaginationRef } from './PrimaryPagination.data';
import { useScrollTransition } from 'util/react-transition-component';
import CircleButton from '../circle-button/CircleButton';

export default function PrimaryPagination({
  total,
  activeIndex,
  onPaginationClick,
  max = 5,
  ref,
  ...props
}: PrimaryPaginationProps & { ref?: Ref<PrimaryPaginationRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const numbersToRender = useMemo(() => {
    const numbers = [];

    let maximum = max;
    if (total < max) {
      maximum = total;
    }

    const offsetMin = activeIndex - Math.floor(maximum / 2);
    const offsetMax = activeIndex + Math.ceil(maximum / 2);

    for (let i = -Math.floor(maximum / 2); i < maximum / 2; i++) {
      let index = activeIndex + i;

      if (offsetMin < 0) {
        index -= offsetMin;
      }

      if (offsetMax > total) {
        index -= offsetMax - total;
      }

      numbers.push(index);
    }

    return numbers;
  }, [total, activeIndex, max]);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.primaryPagination, props.className, {
        [styles.isHidden]: total <= 1,
      })}
    >
      <ul className={classNames(styles.list)}>
        <li className={classNames(styles.listItem)}>
          <CircleButton
            icon="arrow-left"
            revertColor
            hasOutline
            className={classNames(styles.button)}
            onClick={() => {
              onPaginationClick(Math.max(activeIndex - 1, 0));
            }}
            isVisible={{
              onMount: true,
              value: activeIndex > 0 && total > max,
            }}
            disableTriggerInOnScroll
          />
        </li>
        {numbersToRender.map((value) => (
          <li key={value} className={classNames(styles.listItem)}>
            <CircleButton
              label={value + 1}
              isSelected={value === activeIndex}
              revertColor
              className={classNames(styles.button)}
              onClick={() => onPaginationClick(value)}
            />
          </li>
        ))}
        <li className={classNames(styles.listItem)}>
          <CircleButton
            icon="arrow-right"
            revertColor
            hasOutline
            className={classNames(styles.button)}
            onClick={() => {
              onPaginationClick(Math.min(activeIndex + 1, total - 1));
            }}
            isVisible={{
              onMount: true,
              value: activeIndex + 1 < total && total > max,
            }}
            disableTriggerInOnScroll
          />
        </li>
      </ul>
    </div>
  );
}
